"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.findFilter = exports.withReplacedFilter = exports.withoutFilters = exports.withoutFilter = exports.withAddedFilters = exports.withAddedFilter = void 0;
const filter_relations_js_1 = require("./filter-relations.js");
const index_js_1 = require("../../index.js");
/**
 * Returns a function that adds a filter to existing filters or filter relations.
 *
 * @param filter - The filter to add.
 * @returns A function that takes existing filters or filter relations and returns updated filters or filter relations with the new filter added.
 * @group Filter Utilities
 * @example
 * ```ts
 * // Using with an array of filters
 * const originalFilters = [filterByAgeRange];
 * const updatedFilters = withAddedFilter(filterByCost)(originalFilters);
 * // [filterByAgeRange, filterByCost]
 *
 * // Using with filter relations
 * const originalFilterRelations = filterFactory.logic.or(filterByAgeRange, filterByRevenue);
 * const updatedFilterRelations = withAddedFilter(filterByCost)(originalFilterRelations);
 * // (filterByAgeRange OR filterByRevenue) AND filterByCost
 * ```
 */
function withAddedFilter(filter) {
    return (filters) => {
        if ((0, index_js_1.isFilterRelations)(filters)) {
            const { filters: existingFilters, relations } = (0, filter_relations_js_1.splitFiltersAndRelations)(filters);
            const newFilters = [...existingFilters, filter];
            const newRelations = (0, filter_relations_js_1.calculateNewRelations)(existingFilters, relations, newFilters);
            return (0, filter_relations_js_1.combineFiltersAndRelations)(newFilters, newRelations);
        }
        return [...(filters || []), filter];
    };
}
exports.withAddedFilter = withAddedFilter;
/**
 * Returns a function that adds multiple filters to existing filters or filter relations.
 *
 * @param filtersToAdd - An array of filters to add.
 * @returns A function that takes existing filters or filter relations and returns updated filters or filter relations with the new filters added.
 * @group Filter Utilities
 * @example
 * ```ts
 * // Using with an array of filters
 * const originalFilters = [filterByAgeRange];
 * const updatedFilters = withAddedFilters([filterByCost, filterByRevenue])(originalFilters);
 * // [filterByAgeRange, filterByCost, filterByRevenue]
 *
 * // Using with filter relations
 * const originalFilterRelations = filterFactory.logic.or(filterByAgeRange, filterByRevenue);
 * const updatedFilterRelations = withAddedFilters([filterByCost, filterByRevenue])(originalFilterRelations);
 * // (filterByAgeRange OR filterByRevenue) AND filterByCost AND filterByRevenue
 * ```
 */
function withAddedFilters(filtersToAdd) {
    return (filters) => {
        if ((0, index_js_1.isFilterRelations)(filters)) {
            const { filters: existingFilters, relations } = (0, filter_relations_js_1.splitFiltersAndRelations)(filters);
            const newFilters = [...existingFilters, ...filtersToAdd];
            const newRelations = (0, filter_relations_js_1.calculateNewRelations)(existingFilters, relations, newFilters);
            return (0, filter_relations_js_1.combineFiltersAndRelations)(newFilters, newRelations);
        }
        return [...(filters || []), ...filtersToAdd];
    };
}
exports.withAddedFilters = withAddedFilters;
/**
 * Returns a function that removes a filter from existing filters or filter relations.
 *
 * @param filterToRemove - The filter to remove.
 * @returns A function that takes existing filters or filter relations and returns updated filters or filter relations without the specified filter.
 * @group Filter Utilities
 * @example
 * ```ts
 * // Using with an array of filters
 * const originalFilters = [filterByAgeRange, filterByRevenue, filterByCost];
 * const updatedFilters = withoutFilter(filterByCost)(originalFilters);
 * // [filterByAgeRange, filterByRevenue]
 *
 * // Using with filter relations
 * const originalFilterRelations = filterFactory.logic.or(filterByAgeRange, filterByRevenue);
 * const updatedFiltersRelations = withoutFilter(filterByRevenue)(originalFilterRelations);
 * // filterByAgeRange
 * ```
 */
function withoutFilter(filterToRemove) {
    return (filters) => {
        if ((0, index_js_1.isFilterRelations)(filters)) {
            const { filters: existingFilters, relations } = (0, filter_relations_js_1.splitFiltersAndRelations)(filters);
            const newFilters = existingFilters.filter((filter) => filter.config.guid !== filterToRemove.config.guid);
            const newRelations = (0, filter_relations_js_1.calculateNewRelations)(existingFilters, relations, newFilters);
            return (0, filter_relations_js_1.combineFiltersAndRelations)(newFilters, newRelations);
        }
        return (filters || []).filter((filter) => filter.config.guid !== filterToRemove.config.guid);
    };
}
exports.withoutFilter = withoutFilter;
/**
 * Returns a function that removes multiple filters from existing filters or filter relations.
 *
 * @param filtersToRemove - An array of filters to remove.
 * @returns A function that takes existing filters or filter relations and returns updated filters or filter relations without the specified filters.
 * @group Filter Utilities
 * @example
 * ```ts
 * // Using with an array of filters
 * const originalFilters = [filterByAgeRange, filterByRevenue, filterByCost];
 * const updatedFilters = withRemovedFilters([filterByRevenue, filterByCost])(originalFilters);
 * // [filterByAgeRange]
 *
 * // Using with filter relations
 * const originalFilterRelations = filterFactory.logic.or(filterByAgeRange, filterByRevenue);
 * const updatedFiltersRelations = withRemovedFilters([filterByRevenue])(originalFilterRelations);
 * // filterByAgeRange
 * ```
 */
function withoutFilters(filtersToRemove) {
    return (filters) => {
        if ((0, index_js_1.isFilterRelations)(filters)) {
            const { filters: existingFilters, relations } = (0, filter_relations_js_1.splitFiltersAndRelations)(filters);
            const newFilters = existingFilters.filter((filter) => !filtersToRemove.some((filterToRemove) => filter.config.guid === filterToRemove.config.guid));
            const newRelations = (0, filter_relations_js_1.calculateNewRelations)(existingFilters, relations, newFilters);
            return (0, filter_relations_js_1.combineFiltersAndRelations)(newFilters, newRelations);
        }
        return (filters || []).filter((filter) => !filtersToRemove.some((filterToRemove) => filter.config.guid === filterToRemove.config.guid));
    };
}
exports.withoutFilters = withoutFilters;
/**
 * Returns a function that replaces a filter with a new filter in existing filters or filter relations.
 *
 * @param filterToReplace - The filter to replace.
 * @param newFilter - The new filter to use as a replacement.
 * @returns A function that takes existing filters or filter relations and returns updated filters or filter relations with the filter replaced.
 * @group Filter Utilities
 * @example
 * ```ts
 * // Using with an array of filters
 * const originalFilters = [filterByAgeRange, filterByRevenue];
 * const updatedFilters = withReplacedFilter(filterByRevenue, filterByCost)(originalFilters);
 * // [filterByAgeRange, filterByCost]
 *
 * // Using with filter relations
 * const originalFilterRelations = filterFactory.logic.or(filterByAgeRange, filterByRevenue);
 * const updatedFilterRelations = withReplacedFilter(filterByRevenue, filterByCost)(originalFilterRelations);
 * // (filterByAgeRange OR filterByCost)
 * ```
 */
function withReplacedFilter(filterToReplace, newFilter) {
    return (filters) => {
        if ((0, index_js_1.isFilterRelations)(filters)) {
            const { filters: existingFilters, relations } = (0, filter_relations_js_1.splitFiltersAndRelations)(filters);
            const newFilters = existingFilters.map((filter) => filter.config.guid === filterToReplace.config.guid ? newFilter : filter);
            const newRelations = (0, filter_relations_js_1.getRelationsWithReplacedFilter)(relations, filterToReplace, newFilter);
            return (0, filter_relations_js_1.combineFiltersAndRelations)(newFilters, newRelations);
        }
        return (filters || []).map((filter) => filter.config.guid === filterToReplace.config.guid ? newFilter : filter);
    };
}
exports.withReplacedFilter = withReplacedFilter;
/**
 * Finds a filter in an array of filters or filter relations.
 * Returns the first filter that satisfies the provided search function.
 * @group Filter Utilities
 * @param filters - An array of filters or filter relations to search.
 * @param searchFn - A function that takes a filter and returns a boolean indicating whether the filter satisfies the search criteria.
 * @returns The first filter that satisfies the search function, or `undefined` if no filter is found.
 *
 */
function findFilter(filters, searchFn) {
    if (!filters) {
        return undefined;
    }
    const filtersArray = (0, index_js_1.isFilterRelations)(filters) ? (0, filter_relations_js_1.getFiltersArray)(filters) : filters;
    return filtersArray.find(searchFn);
}
exports.findFilter = findFilter;
